/**
 * PROTOTYPE seed data — throwaway. Not production content.
 * Question: does the note-card sermon model feel right?
 */

export type ProtoCard = {
	id: string
	faceTitle: string
	faceSummary: string
	back: string
	presentationTagged: boolean
	labels: string[]
	authorId: string
	authorName: string
	children: ProtoCard[]
}

export const CURRENT_USER = {
	id: 'user-kurt',
	name: 'Kurt Libby',
}

export const OTHER_USER = {
	id: 'user-maya',
	name: 'Maya Chen',
}

let _id = 0
export function nid(prefix = 'c') {
	_id += 1
	return `${prefix}-${_id}`
}

function card(
	partial: Omit<ProtoCard, 'id' | 'children' | 'presentationTagged' | 'labels' | 'authorId' | 'authorName'> &
		Partial<Pick<ProtoCard, 'presentationTagged' | 'labels' | 'authorId' | 'authorName' | 'children'>>,
): ProtoCard {
	return {
		id: nid(),
		presentationTagged: false,
		labels: [],
		authorId: CURRENT_USER.id,
		authorName: CURRENT_USER.name,
		children: [],
		...partial,
	}
}

/** Blank card for “+ New card” / insert-between (`n`). */
export function emptyProtoCard(): ProtoCard {
	return card({
		faceTitle: '',
		faceSummary: '',
		back: '',
	})
}

/** MLK — I Have a Dream (abridged tree for canvas) */
export function seedCanvas(): ProtoCard[] {
	return [
		card({
			faceTitle: 'Opening — promissory note',
			faceSummary: 'Emancipation & the unmet promise',
			back: 'Five score years ago, a great American, in whose symbolic shadow we stand today, signed the Emancipation Proclamation… But one hundred years later, the Negro still is not free.',
			presentationTagged: true,
			children: [
				card({
					faceTitle: 'Bad check',
					faceSummary: 'America has defaulted',
					back: 'It is obvious today that America has defaulted on this promissory note insofar as her citizens of color are concerned. Instead of honoring this sacred obligation, America has given the Negro people a bad check, a check which has come back marked “insufficient funds.”',
					presentationTagged: true,
				}),
				card({
					faceTitle: 'Refuse to believe the bank is bankrupt',
					faceSummary: 'Cash the check of freedom',
					back: 'But we refuse to believe that the bank of justice is bankrupt. We refuse to believe that there are insufficient funds in the great vaults of opportunity of this nation.',
				}),
			],
		}),
		card({
			faceTitle: 'The fierce urgency of now',
			faceSummary: 'No gradualism',
			back: 'We have also come to this hallowed spot to remind America of the fierce urgency of now. This is no time to engage in the luxury of cooling off or to take the tranquilizing drug of gradualism.',
			presentationTagged: true,
			children: [
				card({
					faceTitle: 'Rise from the dark valley',
					faceSummary: 'Racial injustice → justice',
					back: 'Now is the time to rise from the dark and desolate valley of segregation to the sunlit path of racial justice.',
					children: [
						card({
							faceTitle: 'Not drink from bitterness',
							faceSummary: 'Discipline of dignity',
							back: 'In the process of gaining our rightful place we must not be guilty of wrongful deeds. Let us not seek to satisfy our thirst for freedom by drinking from the cup of bitterness and hatred.',
						}),
					],
				}),
			],
		}),
		card({
			faceTitle: 'I have a dream',
			faceSummary: 'The refrain',
			back: 'I say to you today, my friends, so even though we face the difficulties of today and tomorrow, I still have a dream. It is a dream deeply rooted in the American dream.',
			presentationTagged: true,
			children: [
				card({
					faceTitle: 'Content of character',
					faceSummary: 'Judged not by color',
					back: 'I have a dream that my four little children will one day live in a nation where they will not be judged by the color of their skin but by the content of their character. I have a dream today.',
					presentationTagged: true,
				}),
				card({
					faceTitle: 'Every valley exalted',
					faceSummary: 'Isaiah vision',
					back: 'I have a dream that one day every valley shall be exalted, every hill and mountain shall be made low, the rough places will be made plain, and the crooked places will be made straight, and the glory of the Lord shall be revealed, and all flesh shall see it together.',
					presentationTagged: true,
				}),
			],
		}),
		card({
			faceTitle: 'Let freedom ring',
			faceSummary: 'From every mountainside',
			back: 'And when this happens, and when we allow freedom ring, when we let it ring from every village and every hamlet, from every state and every city, we will be able to speed up that day…',
			presentationTagged: true,
			children: [
				card({
					faceTitle: 'Free at last',
					faceSummary: 'Closing cadence',
					back: 'Free at last! Free at last! Thank God Almighty, we are free at last!',
					presentationTagged: true,
				}),
			],
		}),
	]
}

/** Other famous-speech fragments for Someday */
export function seedSomeday(): ProtoCard[] {
	return [
		card({
			faceTitle: 'Four score and seven years ago',
			faceSummary: 'Gettysburg — new nation',
			back: 'Four score and seven years ago our fathers brought forth on this continent, a new nation, conceived in Liberty, and dedicated to the proposition that all men are created equal.',
			labels: ['america', 'dedication'],
			authorId: OTHER_USER.id,
			authorName: OTHER_USER.name,
			children: [
				card({
					faceTitle: 'Unfinished work',
					faceSummary: 'Dedicate / consecrate',
					back: 'It is for us the living, rather, to be dedicated here to the unfinished work which they who fought here have thus far so nobly advanced.',
					labels: ['america'],
					authorId: OTHER_USER.id,
					authorName: OTHER_USER.name,
				}),
			],
		}),
		card({
			faceTitle: 'Blood, toil, tears and sweat',
			faceSummary: 'Churchill — offer nothing but',
			back: 'I would say to the House, as I said to those who have joined this government: I have nothing to offer but blood, toil, tears and sweat.',
			labels: ['courage', 'wartime'],
			authorId: CURRENT_USER.id,
			authorName: CURRENT_USER.name,
		}),
		card({
			faceTitle: 'Ask not',
			faceSummary: 'JFK inaugural',
			back: 'And so, my fellow Americans: ask not what your country can do for you—ask what you can do for your country.',
			labels: ['america', 'call'],
			presentationTagged: true,
			authorId: OTHER_USER.id,
			authorName: OTHER_USER.name,
		}),
		card({
			faceTitle: 'We shall fight on the beaches',
			faceSummary: 'Churchill — never surrender',
			back: 'We shall fight on the beaches, we shall fight on the landing grounds, we shall fight in the fields and in the streets, we shall fight in the hills; we shall never surrender.',
			labels: ['courage', 'wartime'],
			authorId: CURRENT_USER.id,
			authorName: CURRENT_USER.name,
		}),
		card({
			faceTitle: 'The only thing we have to fear',
			faceSummary: 'FDR first inaugural',
			back: 'So, first of all, let me assert my firm belief that the only thing we have to fear is fear itself—nameless, unreasoning, unjustified terror which paralyzes needed efforts to convert retreat into advance.',
			labels: ['courage'],
			authorId: OTHER_USER.id,
			authorName: OTHER_USER.name,
		}),
	]
}
